import React, { useCallback } from 'react';
import Lozenge from '@atlaskit/lozenge';
import Spinner from '@atlaskit/spinner';
import Link from '@atlaskit/link';
import { useIntl } from '@atlassian/jira-intl';
import messages from '../messages.tsx';
import { ERRORS, type ApplinksError } from '../../../../common/status/errors.tsx';
import type { ApplicationLinkV3Status } from '../../../../common/entities.tsx';

export type ApplinkStatusCategory =
	| 'incompatible'
	| 'non-atlassian'
	| 'system'
	| 'network-error'
	| 'creation-failed'
	| 'configuration-incomplete'
	| 'in-progress'
	| 'configuration-complete'
	| 'submission-failed'
	| 'access-denied'
	| 'config-error'
	| 'deprecated-status'
	| 'disabled-status'
	| 'connected-status';

type StatusLozengeLoadingProps = {
	kind: 'loading';
};

type StatusLozengeErrorProps = {
	kind: 'error';
	err: ApplinksError;
	authorisationUri?: string;
};

type StatusLozengeWorkingProps = {
	kind: 'working';
};

type StatusLozengeNotConnectedProps = {
	kind: 'not-connected';
};

type StatusLozengeProps =
	| StatusLozengeLoadingProps
	| StatusLozengeErrorProps
	| StatusLozengeWorkingProps
	| StatusLozengeNotConnectedProps;

// Use StatusLozengeV3 if you have an ApplicationLinkV3Status, otherwise
// use this one to fake it (e.g. for the creation flow when we don't have
// an applink yet)
export const StatusLozenge = (props: StatusLozengeProps) => {
	const { formatMessage } = useIntl();

	if (props.kind === 'loading') {
		return <Spinner size="small" />;
	}

	if (props.kind === 'working') {
		return <Lozenge appearance="success">{formatMessage(messages.connectedStatus)}</Lozenge>;
	}

	if (props.kind === 'not-connected') {
		return <Lozenge>{formatMessage(messages.notConnectedStatus)}</Lozenge>;
	}

	if (props.kind === 'error') {
		const { err } = props;

		if (err.category === 'ACCESS_ERROR') {
			if (err.type === 'INSUFFICIENT_REMOTE_PERMISSION') {
				return <Lozenge appearance="moved">{formatMessage(messages.accessDeniedStatus)}</Lozenge>;
			}
			// Authenticated status API -> prompt user to do OAuth dance
			if (props.authorisationUri) {
				return (
					<Link href={props.authorisationUri}>{formatMessage(messages.authTokenRequired)}</Link>
				);
			}
			// Fallback for ACCESS_ERROR cases that don't match above conditions
			return <Lozenge appearance="removed">{formatMessage(messages.accessDeniedStatus)}</Lozenge>;
		}

		switch (err.type) {
			case 'INCOMPATIBLE':
				return <Lozenge appearance="default">{formatMessage(messages.incompatibleStatus)}</Lozenge>;
			case 'NON_ATLASSIAN':
				return <Lozenge appearance="default">{formatMessage(messages.nonAtlassianStatus)}</Lozenge>;
			case 'SYSTEM':
				return <Lozenge appearance="default">{formatMessage(messages.systemStatus)}</Lozenge>;
			case 'NETWORK_ERROR':
			case 'UNKNOWN':
				return <Lozenge appearance="removed">{formatMessage(messages.networkErrorStatus)}</Lozenge>;
			case 'CREATION_FAILED':
				return (
					<Lozenge appearance="removed">{formatMessage(messages.creationFailedStatus)}</Lozenge>
				);
			case 'CONFIGURATION_INCOMPLETE':
				return (
					<Lozenge appearance="removed">
						{formatMessage(messages.configurationIncompleteStatus)}
					</Lozenge>
				);
			case 'IN_PROGRESS':
				return <Lozenge appearance="moved">{formatMessage(messages.inProgressStatus)}</Lozenge>;
			case 'CONFIGURATION_COMPLETE':
				return (
					<Lozenge appearance="inprogress">{formatMessage(messages.configurationComplete)}</Lozenge>
				);
			case 'SUBMISSION_FAILED':
				return <Lozenge appearance="removed">{formatMessage(messages.submissionFailed)}</Lozenge>;
			case 'DEPRECATED':
				return (
					<Lozenge appearance="inprogress">{formatMessage(messages.deprecatedStatus)}</Lozenge>
				);
			case 'DISABLED':
				return <Lozenge appearance="default">{formatMessage(messages.disabledStatus)}</Lozenge>;
			case 'CONFIG_ERROR':
			default:
				return <Lozenge appearance="removed">{formatMessage(messages.configErrorStatus)}</Lozenge>;
		}
	}
};

type StatusLozengeV3Props = {
	status?: ApplicationLinkV3Status | null;
};

export const StatusLozengeV3 = (props: StatusLozengeV3Props) => {
	const wrapWithV3Status = useCallback(() => {
		if (!props.status) {
			return <StatusLozenge kind="loading" />;
		}

		if (props.status.working) {
			return <StatusLozenge kind="working" />;
		}

		const err = props.status.error ?? ERRORS.UNKNOWN;
		return <StatusLozenge kind="error" err={err} />;
	}, [props.status]);

	return wrapWithV3Status();
};
